/**
* Site configuration — Thai Notary Law & Service Co., Ltd.
*
* Notary Public services + certified translation + consular/embassy
* legalization across Thailand. TH is default; site publishes 7 locales.
*/
export const defaultSiteConfig = {
  id: "default",

  // ── Brand ──────────────────────────────────────────────────────────────
  name: "Thai Notary Law",
  legalName: "Thai Notary Law & Service Co., Ltd.",
  tagline: "บริการรับรองเอกสารโดยทนาย Notary Public — ทั่วประเทศไทยและทั่วโลก",
  description:
    "บริการรับรองเอกสารโดยทนาย Notary Public ที่ได้รับใบอนุญาตจากสภาทนายความ พร้อมบริการแปลเอกสารทุกภาษา รับรองกงสุลและสถานทูต ครอบคลุม 77 จังหวัด 50 เขตในกรุงเทพฯ และให้บริการออนไลน์ทั่วโลก",
  defaultTitle: "Thai Notary Law — สำนักงานกฎหมาย ทนาย Notary Public กรุงเทพ",
  defaultDescription:
    "สำนักงานกฎหมายครบวงจรในกรุงเทพ บริการทนาย Notary Public รับรองเอกสาร แปลเอกสารทุกภาษา รับรองกงสุลและสถานทูต พร้อมงานกฎหมายธุรกิจและครอบครัว ทั่วประเทศไทย โทร 094-895-8999",


  /**
   * When set, canonical URLs and feeds pin to this host regardless of the
   * incoming request. Leave empty to fall back to the request host.
   */
  canonicalHost: "thainotarylaw.com",
  url: "/",
  logoPath: "/__l5e/assets-v1/6fb5ad95-24ee-4ea6-801c-65fbd1d70933/thai-notary-law-logo.png",
  locale: "th_TH",
  language: "th",
  themeColor: "#0A1F44",
  founded: 2022,

  // ── Multi-locale / hreflang ────────────────────────────────────────────
  locales: [
    { code: "th", hrefLang: "th-TH", pathPrefix: "", host: "" },
    { code: "en", hrefLang: "en", pathPrefix: "/en", host: "" },
    { code: "zh", hrefLang: "zh-CN", pathPrefix: "/zh", host: "" },
    { code: "ja", hrefLang: "ja-JP", pathPrefix: "/ja", host: "" },
    { code: "ko", hrefLang: "ko-KR", pathPrefix: "/ko", host: "" },
    { code: "vi", hrefLang: "vi-VN", pathPrefix: "/vi", host: "" },
    { code: "id", hrefLang: "id-ID", pathPrefix: "/id", host: "" },
  ] as Array<{ code: string; hrefLang: string; pathPrefix: string; host: string }>,
  defaultLocale: "th",
  /** Locale advertised as `x-default` in hreflang (English for global reach). */
  xDefaultLocale: "en",

  contact: {
    email: "Contact@thainotary.co.th",
    phone: "+66-94-895-8999",
    lineId: "@Thainotary",
    lineUrl: "https://lin.ee/YvaJ88DZ",
    address: {
      streetAddress: "61 Soi Lat Phrao 95 (Prang Thip)",
      addressLocality: "Wang Thonglang",
      addressRegion: "Bangkok",
      postalCode: "10310",
      addressCountry: "TH",
    },
    hoursSpec: "Mo-Su 09:00-18:00",
    geo: { latitude: 13.7807, longitude: 100.6019 },
    registrationNumber: "0405565001923",
  },

  social: {
    twitter: "",
    linkedin: "",
    facebook: "https://www.facebook.com/thainotarylaw",
    youtube: "",
    github: "",
    line: "https://lin.ee/YvaJ88DZ",
  },

  verification: {
    google: "",
    bing: "",
    yandex: "",
    pinterest: "",
  },

  /**
   * Google Search Console property used by the admin dashboards and the
   * scheduled snapshots. Must be an exact identifier Google returned from
   * GET /webmasters/v3/sites — never built from the site URL.
   */
  searchConsole: {
    siteUrl: "https://thainotarylaw.com/",
  },


  legal: {
    privacyUrl: "/legal/privacy",
    termsUrl: "/legal/terms",
    cookiesUrl: "/legal/cookies",
    disclaimerUrl: "/legal/disclaimer",
    editorialUrl: "/editorial-standards",
    securityContact: "Contact@thainotary.co.th",
  },

  /** JSON-LD @type for the sitewide Organization node. */
  schemaDefaults: {
    organizationType: "LegalService" as
      | "Organization"
      | "LegalService"
      | "LocalBusiness"
      | "ProfessionalService",
  },

  aeo: {
    speakableSelectors: [
      "h1",
      ".speakable-summary",
      ".answer-summary",
      ".lead",
      ".faq-answer",
      "article p:first-of-type",
      "[data-speakable]",
    ],
  },

  indexNow: {
    engines: [
      "https://api.indexnow.org/indexnow",
      "https://www.bing.com/indexnow",
      "https://yandex.com/indexnow",
    ],
  },
};

export type SiteConfig = typeof defaultSiteConfig;
